import { cn } from '@/lib/format';

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

export default function Pagination({ page, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages: number[] = [];
  const start = Math.max(1, Math.min(page - 1, totalPages - 2));
  const end = Math.min(totalPages, start + 2);
  for (let i = start; i <= end; i += 1) pages.push(i);

  const btnBase =
    'min-w-9 h-9 px-3 inline-flex items-center justify-center rounded-md text-sm font-label font-bold transition-colors cursor-pointer whitespace-nowrap';

  return (
    <nav aria-label="分页" className="flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className={cn(
          btnBase,
          'border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700',
          page === 1 && 'opacity-40 cursor-not-allowed hover:border-background-300 hover:text-foreground-700',
        )}
      >
        <i className="ri-arrow-left-s-line" />
        上一页
      </button>

      {start > 1 && (
        <>
          <button type="button" onClick={() => onChange(1)} className={cn(btnBase, 'border border-background-300 text-foreground-700 hover:border-primary-400')}>
            1
          </button>
          {start > 2 && <span className="px-1 text-foreground-400">…</span>}
        </>
      )}

      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={cn(
            btnBase,
            p === page
              ? 'bg-primary-500 text-background-50'
              : 'border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700',
          )}
        >
          {p}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="px-1 text-foreground-400">…</span>}
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            className={cn(btnBase, 'border border-background-300 text-foreground-700 hover:border-primary-400')}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className={cn(
          btnBase,
          'border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700',
          page === totalPages && 'opacity-40 cursor-not-allowed hover:border-background-300 hover:text-foreground-700',
        )}
      >
        下一页
        <i className="ri-arrow-right-s-line" />
      </button>
    </nav>
  );
}